import { AccessLevel } from "@/api/williams/users";
import { ConfirmationDialog } from "@/components/dialogs";
import { faBan } from "@fortawesome/free-solid-svg-icons";
import { faFilePdf, faSave, faTrashAlt } from "@fortawesome/free-regular-svg-icons";
import { IconButton } from "../../components";
import { TestEvent, TestEventStatus } from "@/api/williams/testEvents";
import { useCancelTestEvent, useDeleteTestEvent, usePrintTestEvent, useSaveTestEvent } from "../hooks";
import { useFormContext } from "react-hook-form";
import { useLoadingScreen, useModalDialog } from "@/hooks";
import { usePermissions } from "../../users/hooks";

export function TEFormToolbar({ data }: { data: Readonly<TestEvent> }) {
  const form = useFormContext<TestEvent>();
  const dialog = useModalDialog();
  const save = useSaveTestEvent();
  const cancel = useCancelTestEvent();
  const deleteEvent = useDeleteTestEvent(true);
  const print = usePrintTestEvent();
  const permissions = usePermissions();
  const loader = useLoadingScreen();

  const isDirty = Object.keys(form.formState.dirtyFields).length > 0;

  function handleSave(data: TestEvent) {
    save.mutateAsync(data, {
      onSuccess: (updatedData) => form.reset(updatedData),
    });
  }

  function handleDelete() {
    dialog.show(ConfirmationDialog, {
      caption: "Delete Test Event",
      message: "Are you sure you want to delete this test event?",
      onAccept: () => {
        loader.show();
        deleteEvent.mutate(data.id);
      },
    });
  }

  function handleCancel() {
    dialog.show(ConfirmationDialog, {
      caption: "Cancel Event",
      message: "You will no longer be able to edit, this cancels the test event from the project",
      onAccept: () => {
        loader.show();
        cancel.mutate(data, {
          onSuccess: (updatedData) => form.reset(updatedData),
          onSettled: () => loader.hide(),
        });
      },
    });
  }

  const handlePrint = () => {
    if (!isDirty) {
      print.showDialog(data);
    } else {
      dialog.show(ConfirmationDialog, {
        caption: "Unsaved Changes",
        message: "You have unsaved changes, would you like to save them now?",
        onAccept: () => {
          save.mutate(data, {
            onSuccess: (updatedData) => {
              form.reset(updatedData);
              print.showDialog(updatedData);
            },
          });
        },
      });
    }
  };

  return (
    <div className="d-flex gap-2 my-auto">
      <IconButton
        icon={faSave}
        title="Save"
        onClick={form.handleSubmit(handleSave)}
        disabled={!isDirty || data.locked}
        isLoading={save.isLoading}
        hidden={permissions.data?.accessLevel === AccessLevel.GeneralUser}
      />
      <IconButton
        icon={faBan}
        title="Cancel"
        onClick={handleCancel}
        disabled={data.locked}
        isLoading={cancel.isLoading}
        hidden={permissions.data?.accessLevel === AccessLevel.GeneralUser}
      />
      <IconButton
        icon={faTrashAlt}
        title="Delete"
        onClick={handleDelete}
        disabled={data.locked}
        isLoading={deleteEvent.isLoading}
        hidden={permissions.data && permissions.data.accessLevel < AccessLevel.AIWeldingSME}
      />
      <IconButton
        icon={faFilePdf}
        title="Download PDF"
        disabled={data.status === TestEventStatus.New}
        onClick={form.handleSubmit(handlePrint)}
      />
    </div>
  );
}
